import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff, Wifi } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * Check actual internet connectivity by making a lightweight request.
 * navigator.onLine is VERY unreliable - it can return false even when online.
 *
 * Strategy: Always try to fetch, ignore navigator.onLine completely for "online" detection.
 * Only trust navigator.onLine = false as a hint, but verify with fetch anyway.
 */
async function checkActualConnectivity(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    // Fetch the current page or a known endpoint
    // Using HEAD request to minimize data transfer
    const response = await fetch(window.location.origin + '/?_ping=' + Date.now(), {
      method: 'HEAD',
      cache: 'no-store',
      mode: 'no-cors', // Avoid CORS issues, we just care if request succeeds
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    // With no-cors mode, response.ok is always false, but if we get here without error, we're online
    return true;
  } catch {
    // Network error or timeout = offline
    return false;
  }
}

/**
 * OfflineIndicator - Shows online/offline status with smooth animations
 *
 * Features:
 * - Detects offline status using actual network requests (not unreliable navigator.onLine)
 * - Shows banner when offline
 * - Brief toast when coming back online
 * - Non-intrusive, dismissible
 * - Mobile-optimized positioning
 */
export function OfflineIndicator() {
  const { t } = useTranslation('common');
  // Start with true - assume online until proven otherwise
  const [isOnline, setIsOnline] = useState(true);
  const [showOnlineToast, setShowOnlineToast] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const wasOfflineRef = useRef(false);
  const checkInProgressRef = useRef(false);

  useEffect(() => {
    const verifyConnectivity = async () => {
      // Prevent concurrent checks
      if (checkInProgressRef.current) return;
      checkInProgressRef.current = true;

      try {
        const online = await checkActualConnectivity();

        // If we were offline and now online, show toast
        if (online && wasOfflineRef.current) {
          setShowOnlineToast(true);
          setTimeout(() => setShowOnlineToast(false), 3000);
        }

        wasOfflineRef.current = !online;
        setIsOnline(online);
        if (!online) {
          setIsDismissed(false);
        }
      } finally {
        checkInProgressRef.current = false;
      }
    };

    // Initial connectivity check after a delay
    const initTimer = setTimeout(async () => {
      await verifyConnectivity();
      setHasInitialized(true);
    }, 2000);

    // Listen to browser events as triggers to re-verify
    const handleOnline = () => {
      verifyConnectivity();
    };

    const handleOffline = () => {
      // Don't immediately show offline - verify first
      // navigator.onLine is unreliable
      verifyConnectivity();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic check every 30 seconds when visible
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (!intervalId) {
        intervalId = setInterval(verifyConnectivity, 30000);
      }
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        verifyConnectivity();
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (!document.hidden) {
      startPolling();
    }

    return () => {
      clearTimeout(initTimer);
      stopPolling();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Don't show anything if online and no toast
  if (isOnline && !showOnlineToast) {
    return null;
  }

  // Don't show offline banner during initialization (first 1 second)
  // This prevents false positives during app startup
  if (!isOnline && !hasInitialized) {
    return null;
  }

  // Don't show offline banner if dismissed
  if (!isOnline && isDismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      {!isOnline ? (
        // Offline banner - sticky at top
        <motion.div
          key="offline-banner"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white shadow-lg"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <WifiOff className="h-5 w-5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{t('offline.title', 'Offline')}</p>
                <p className="text-xs opacity-90 mt-0.5">
                  {t('offline.message', 'Changes will be synced when you are back online')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsDismissed(true)}
              className="ml-4 p-1 rounded hover:bg-white/20 transition-colors"
              aria-label={t('accessibility.close')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </motion.div>
      ) : showOnlineToast ? (
        // Online toast - bottom right
        <motion.div
          key="online-toast"
          initial={{ x: 400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 400, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-4 right-4 z-[100] bg-green-500 text-white rounded-lg shadow-lg px-4 py-3 max-w-sm"
          style={{
            marginBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div className="flex items-center gap-3">
            <Wifi className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{t('offline.backOnline', 'Back online')}</p>
              <p className="text-xs opacity-90 mt-0.5">
                {t('offline.backOnlineMessage', 'Changes are now being synced')}
              </p>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Hook to check online/offline status with actual connectivity verification
 * Usage: const isOnline = useOnlineStatus();
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    // Initial check after short delay
    const timer = setTimeout(() => {
      checkActualConnectivity().then(setIsOnline);
    }, 1000);

    const handleOnline = async () => {
      const online = await checkActualConnectivity();
      setIsOnline(online);
    };

    const handleOffline = async () => {
      // Don't trust navigator.onLine, verify with fetch
      const online = await checkActualConnectivity();
      setIsOnline(online);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
